"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import RedirectLoadingOverlay from "@/components/RedirectLoadingOverlay";
import {
  registerRedirectLoadingHandlers,
  REDIRECT_MESSAGES,
} from "@/libs/redirectLoading";

interface RedirectLoadingContextValue {
  isRedirecting: boolean;
  message: string;
  showRedirectLoading: (message?: string) => void;
  hideRedirectLoading: () => void;
}

const RedirectLoadingContext = createContext<
  RedirectLoadingContextValue | undefined
>(undefined);

export function RedirectLoadingProvider({ children }: { children: ReactNode }) {
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [message, setMessage] = useState<string>(REDIRECT_MESSAGES.redirecting);

  const showRedirectLoading = useCallback((nextMessage?: string) => {
    setMessage(nextMessage ?? REDIRECT_MESSAGES.redirecting);
    setIsRedirecting(true);
  }, []);

  const hideRedirectLoading = useCallback(() => {
    setIsRedirecting(false);
  }, []);

  useEffect(() => {
    return registerRedirectLoadingHandlers({
      show: showRedirectLoading,
      hide: hideRedirectLoading,
    });
  }, [showRedirectLoading, hideRedirectLoading]);

  const value = useMemo<RedirectLoadingContextValue>(
    () => ({
      isRedirecting,
      message,
      showRedirectLoading,
      hideRedirectLoading,
    }),
    [isRedirecting, message, showRedirectLoading, hideRedirectLoading]
  );

  return (
    <RedirectLoadingContext.Provider value={value}>
      {children}
      {isRedirecting && <RedirectLoadingOverlay message={message} />}
    </RedirectLoadingContext.Provider>
  );
}

export function useRedirectLoading(): RedirectLoadingContextValue {
  const ctx = useContext(RedirectLoadingContext);
  if (!ctx) {
    throw new Error(
      "useRedirectLoading must be used within a RedirectLoadingProvider"
    );
  }
  return ctx;
}
